/**
 * The seller's Google Workspace connection, which a plan's Sheet needs (it is
 * created in their own Drive). Connecting happens in a new tab, so the status
 * is checked again when the seller comes back to this one.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getGoogleConnectionStatus } from '../../../services/api';

/** Open the Google Workspace connect flow in a new tab. */
export function connectGoogleWorkspace(): void {
  const api = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  window.open(`${api}/auth/google`, '_blank', 'noopener');
}

/** `connected` is null while the first check runs. */
export function useGoogleConnection(): { connected: boolean | null; recheck: () => void } {
  const [connected, setConnected] = useState<boolean | null>(null);
  const mounted = useRef(true);
  // Set on every mount: StrictMode runs this cleanup once on its simulated unmount in development.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const check = useCallback(async (force: boolean) => {
    try {
      const status = await getGoogleConnectionStatus({ force });
      if (mounted.current) setConnected(!!status?.connected && !status?.expired);
    } catch {
      if (mounted.current) setConnected(false);
    }
  }, []);

  useEffect(() => {
    void check(false);
  }, [check]);

  // Back from the connect tab: look again.
  useEffect(() => {
    if (connected !== false) return undefined;
    const onFocus = () => void check(true);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [connected, check]);

  const recheck = useCallback(() => void check(true), [check]);
  return { connected, recheck };
}
